import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const siteSlug = searchParams.get("site_slug");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [clientToken, setClientToken] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 8) {
      setStatus("failed");
      return;
    }
    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        setClientToken(data.client_token);
        setStatus("paid");
      } else if (data.status === "expired") {
        setStatus("failed");
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2500);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-sy-bg flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center animate-fadeInUp">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-gray-300 animate-spin mb-6" />
            <h1 className="text-2xl font-black font-heading text-[#0A0A0A] mb-2">Verificando pago</h1>
            <p className="text-gray-500 text-sm">Estamos confirmando tu pago, por favor espera...</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-20 h-20 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-[#047857]" />
            </div>
            <h1 className="text-2xl font-black font-heading text-[#0A0A0A] mb-2">
              ¡Pago exitoso!
            </h1>
            <p className="text-gray-500 text-sm mb-8">
              Tu sitio web está activo. Ahora puedes personalizarlo con inteligencia artificial.
            </p>
            {clientToken ? (
              <button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate(`/client/${clientToken}`)}
                className="w-full py-3.5 bg-[#0A0A0A] text-white rounded-2xl font-bold text-base hover:bg-gray-800 transition-colors"
              >
                Ir a mi panel
              </button>
            ) : (
              <p className="text-sm text-gray-400">Configurando tu panel...</p>
            )}
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-20 h-20 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-500" />
            </div>
            <h1 className="text-2xl font-black font-heading text-[#0A0A0A] mb-2">
              Pago no confirmado
            </h1>
            <p className="text-gray-500 text-sm mb-8">
              No pudimos confirmar tu pago. Si realizaste el pago, por favor contacta soporte.
            </p>
            {siteSlug && (
              <button
                onClick={() => navigate(`/pv/${siteSlug}`)}
                className="w-full py-3.5 bg-gray-100 text-gray-700 rounded-2xl font-bold text-base hover:bg-gray-200 transition-colors"
              >
                Volver a la vista previa
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
